import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Timer, Calendar, FileCheck } from "lucide-react";
import { SectionHeader } from "./SovereignPace";

export const TransitionStrike = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        endpoints.transitionCountdown().then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return null;

    const { eas_date, days_remaining, progress_pct, milestones } = data;

    return (
        <section
            id="transition-strike"
            data-testid="transition-strike"
            className="relative py-20 border-b border-zinc-900"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-8">
                <SectionHeader
                    eyebrow="MODULE · 004"
                    title="The Transition Strike"
                    subtitle="180-day countdown · Terminal leave · SkillBridge · TAP doc synthesis."
                />

                <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-10">
                    <div className="col-span-12 lg:col-span-5 border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 relative corner-bracket">
                        <div className="flex items-center gap-2">
                            <Timer className="w-4 h-4 text-gold-300 animate-pulse" />
                            <span className="label-tactical">EAS COUNTDOWN · LIVE</span>
                        </div>

                        <div className="mt-8 text-center">
                            <div
                                data-testid="countdown-days"
                                className="text-7xl lg:text-8xl font-black text-gold-300 tracking-tighter leading-none"
                            >
                                {days_remaining}
                            </div>
                            <div className="badge-tier text-zinc-400 mt-2">DAYS TO EAS</div>
                            <div className="mt-2 text-zinc-500 text-xs uppercase tracking-[0.2em]">
                                Target · {eas_date}
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="flex items-center justify-between mb-2">
                                <span className="badge-tier text-zinc-500">TRANSITION ARC</span>
                                <span className="badge-tier text-gold-300">{progress_pct}%</span>
                            </div>
                            <div className="relative h-3 bg-zinc-900 border border-zinc-800">
                                <div
                                    className="absolute top-0 left-0 h-full bg-gold-300"
                                    style={{ width: `${progress_pct}%` }}
                                />
                                <div
                                    className="absolute top-0 h-full w-0.5 bg-white"
                                    style={{ left: `${progress_pct}%` }}
                                />
                            </div>
                            <div className="flex justify-between mt-2 badge-tier text-zinc-500">
                                <span>D-180</span>
                                <span>D-90</span>
                                <span>D-DAY</span>
                            </div>
                        </div>

                        <div className="mt-8 grid grid-cols-2 gap-3">
                            <KeyMetric
                                label="Terminal Leave"
                                value="60d MAX"
                                icon={Calendar}
                            />
                            <KeyMetric
                                label="SkillBridge"
                                value="180d MAX"
                                icon={FileCheck}
                            />
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-7 border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 relative">
                        <div className="label-tactical">TAP MILESTONE BATTLEFIELD</div>
                        <div className="mt-6 space-y-2">
                            {(milestones || []).map((m, i) => {
                                const due = m.due_offset_days;
                                const reached = days_remaining <= due;
                                return (
                                    <div
                                        key={i}
                                        data-testid={`milestone-${i}`}
                                        className={`flex items-center gap-4 border ${
                                            reached
                                                ? "border-gold-300/40 bg-gold-300/5"
                                                : "border-zinc-800/80"
                                        } px-4 py-3`}
                                    >
                                        <div
                                            className={`w-10 h-10 border flex items-center justify-center ${
                                                reached ? "border-gold-300 text-gold-300" : "border-zinc-800 text-zinc-600"
                                            }`}
                                        >
                                            <span className="badge-tier">{String(i + 1).padStart(2, "0")}</span>
                                        </div>
                                        <div className="flex-1">
                                            <div className={`text-sm font-bold uppercase tracking-wide ${
                                                reached ? "text-gold-300" : "text-white"
                                            }`}>
                                                {m.label}
                                            </div>
                                            <div className="badge-tier text-zinc-500 mt-1">
                                                D-{due} · {reached ? "WINDOW OPEN" : "AHEAD"}
                                            </div>
                                        </div>
                                        <div
                                            className={`badge-tier px-2 py-1 border ${
                                                reached
                                                    ? "border-gold-300/60 text-gold-300"
                                                    : "border-zinc-700 text-zinc-500"
                                            }`}
                                        >
                                            {reached ? "ENGAGE" : "STAGED"}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-6 border-t border-zinc-800/80 pt-4 grid grid-cols-3 gap-4">
                            <SmallStat label="Auto-synth" value="TAP" sub="DD-2648" />
                            <SmallStat label="VA Pre-File" value="BDD" sub="180-90d" />
                            <SmallStat label="DD-214 Audit" value="T-14d" sub="OUTPROCESS" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const KeyMetric = ({ label, value, icon: Icon }) => (
    <div className="border border-zinc-800/80 px-4 py-3">
        <div className="flex items-center gap-2">
            <Icon className="w-3.5 h-3.5 text-gold-300" />
            <span className="badge-tier text-zinc-500">{label.toUpperCase()}</span>
        </div>
        <div className="text-lg font-black text-white mt-1">{value}</div>
    </div>
);

const SmallStat = ({ label, value, sub }) => (
    <div>
        <div className="badge-tier text-zinc-500">{label.toUpperCase()}</div>
        <div className="text-lg font-black text-gold-300 mt-1">{value}</div>
        <div className="badge-tier text-zinc-600 mt-0.5">{sub}</div>
    </div>
);

export default TransitionStrike;
